import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Plus, ShieldCheck, LogOut, Loader2, Edit3, Star, Search } from 'lucide-react';
import { adminApi, logoutAdmin } from './adminApi';
import { toAssetUrl } from '@/data/runtimeApi';

const toUrl = toAssetUrl;

export default function AdminProyectos() {
  const [items, setItems] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [projRes, svcRes] = await Promise.all([
        adminApi.get('/proyectos'),
        adminApi.get('/servicios'),
      ]);
      setItems(projRes.data.items || []);
      setServicios(svcRes.data.items || []);
      if (!newCategory && svcRes.data.items?.length) setNewCategory(svcRes.data.items[0].slug);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (filter !== 'all') arr = arr.filter((p) => p.category === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((p) =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.client || '').toLowerCase().includes(q) ||
        (p.slug || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [items, filter, search]);

  const move = async (globalIdx, dir) => {
    const to = globalIdx + dir;
    if (to < 0 || to >= items.length) return;
    const arr = [...items];
    [arr[globalIdx], arr[to]] = [arr[to], arr[globalIdx]];
    setItems(arr);
    await adminApi.post('/proyectos/reorder', arr.map((x) => x.id));
  };

  const del = async (p) => {
    const msg = `¿Eliminar el proyecto "${p.title}"?\n\n⚠️  Se eliminarán ${(p.gallery || []).length} imagen(es) subidas al servidor. Esta acción no se puede deshacer.\n\n¿Continuar?`;
    if (!window.confirm(msg)) return;
    await adminApi.delete(`/proyectos/${p.id}`);
    load();
  };

  const create = async () => {
    if (!newSlug.trim() || !newTitle.trim() || !newCategory) { alert('Completa título, slug y categoría'); return; }
    setCreating(true);
    try {
      const { data } = await adminApi.post('/proyectos', { slug: newSlug.trim(), title: newTitle.trim(), category: newCategory });
      setNewSlug(''); setNewTitle(''); setShowCreate(false);
      window.location.href = `/admin/proyectos/${data.id}`;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al crear');
    } finally { setCreating(false); }
  };

  const onLogout = () => { logoutAdmin(); window.location.href = '/admin/login'; };

  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="admin-proyectos-page">
      <header className="bg-white border-b border-[#58677A]/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#58677A] hover:text-[#ED6B24] transition"><ArrowLeft size={18}/></Link>
            <div className="w-9 h-9 rounded-lg bg-[#ED6B24]/10 flex items-center justify-center"><ShieldCheck className="text-[#ED6B24]" size={18} /></div>
            <div>
              <p className="text-[15px] font-bold text-[#58677A] leading-tight" style={{fontFamily:'Gotham,sans-serif'}}>Proyectos y galerías</p>
              <p className="text-[11px] text-[#58677A]/60">Home destacados + páginas de categoría + detalle</p>
            </div>
          </div>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-xs text-[#58677A] hover:text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50" data-testid="admin-logout"><LogOut size={13}/> Salir</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Proyectos ({items.length})</h1>
            <p className="text-xs text-[#58677A]/70 mt-0.5">Filtra por categoría, edita textos, imágenes y galería. La estrella indica proyectos destacados en Home.</p>
          </div>
          <button onClick={() => setShowCreate((v) => !v)} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-lg px-4 py-2" data-testid="add-proyecto-btn"><Plus size={15}/> Nuevo proyecto</button>
        </div>

        {showCreate && (
          <div className="bg-white rounded-2xl border border-[#58677A]/10 p-5" data-testid="create-proyecto-panel">
            <h2 className="font-bold text-[#58677A] mb-3" style={{fontFamily:'Gotham,sans-serif'}}>Crear nuevo proyecto</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#58677A]/70 block mb-1">Título</label>
                <input value={newTitle} onChange={(e) => { setNewTitle(e.target.value); if (!newSlug) setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'')); }} placeholder="ej. Apertura tienda Marathon" className={inputCls} data-testid="new-title"/>
              </div>
              <div>
                <label className="text-xs text-[#58677A]/70 block mb-1">Slug (URL)</label>
                <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="apertura-tienda-marathon" className={`${inputCls} font-mono text-sm`} data-testid="new-slug"/>
              </div>
              <div>
                <label className="text-xs text-[#58677A]/70 block mb-1">Categoría</label>
                <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className={inputCls} data-testid="new-category">
                  {servicios.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="text-sm px-4 py-2 rounded-lg text-[#58677A] hover:bg-[#58677A]/10">Cancelar</button>
              <button onClick={create} disabled={creating} className="text-sm px-4 py-2 rounded-lg bg-[#ED6B24] hover:bg-[#ff8242] text-white disabled:opacity-60" data-testid="create-proyecto-confirm">{creating ? 'Creando…' : 'Crear y editar'}</button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 bg-white rounded-2xl border border-[#58677A]/10 p-3">
          <div className="flex items-center gap-1.5 flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="text-[#58677A]/50 ml-1"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título, cliente o slug…" className="w-full text-sm bg-transparent focus:outline-none text-[#58677A]" data-testid="proyectos-search"/>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <FilterChip active={filter==='all'} onClick={() => setFilter('all')} count={items.length} label="Todos"/>
            {servicios.map((s) => (
              <FilterChip key={s.slug} active={filter===s.slug} onClick={() => setFilter(s.slug)} count={items.filter((p) => p.category === s.slug).length} label={s.name}/>
            ))}
          </div>
        </div>

        {loading && <div className="p-8 text-center text-[#58677A]/60 bg-white rounded-2xl border border-[#58677A]/10"><Loader2 className="mx-auto animate-spin mb-2"/>Cargando…</div>}

        {!loading && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => {
              const globalIdx = items.findIndex((x) => x.id === p.id);
              return (
                <li key={p.id} className="bg-white rounded-2xl border border-[#58677A]/10 overflow-hidden group" data-testid={`proyecto-card-${p.slug}`}>
                  <div className="aspect-[4/3] bg-[#f4f5f7] relative overflow-hidden">
                    {p.mainImage ? (
                      <img src={toUrl(p.mainImage)} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#58677A]/40 text-xs">Sin imagen</div>
                    )}
                    {p.featuredOnHome && (
                      <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold bg-[#ED6B24] text-white rounded-full px-2 py-1">
                        <Star size={10} fill="currentColor"/> DESTACADO
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-mono rounded px-1.5 py-0.5">#{globalIdx + 1}</div>
                  </div>
                  <div className="p-3.5">
                    <p className="text-[11px] uppercase tracking-wider text-[#ED6B24] font-bold mb-1">{p.category}</p>
                    <p className="font-bold text-[#58677A] text-[14px] leading-snug line-clamp-2" style={{fontFamily:'Gotham,sans-serif'}}>{p.title}</p>
                    <p className="text-xs text-[#58677A]/60 mt-0.5">{p.client || '—'}</p>
                    <p className="text-[11px] text-[#58677A]/50 mt-1 font-mono truncate">/{p.slug}</p>
                    <p className="text-[11px] text-[#58677A]/60 mt-1">{(p.gallery || []).length} imagen(es) en galería</p>
                    <div className="mt-3 flex items-center gap-1.5">
                      <button onClick={() => move(globalIdx, -1)} disabled={globalIdx===0} className="w-8 h-8 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" title="Subir" data-testid={`up-${p.slug}`}><ArrowUp size={13}/></button>
                      <button onClick={() => move(globalIdx, 1)} disabled={globalIdx===items.length-1} className="w-8 h-8 rounded flex items-center justify-center text-[#58677A] hover:bg-[#ED6B24]/10 disabled:opacity-30" title="Bajar" data-testid={`down-${p.slug}`}><ArrowDown size={13}/></button>
                      <Link to={`/admin/proyectos/${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#ED6B24]/10 hover:bg-[#ED6B24] hover:text-white text-[#ED6B24] text-xs font-semibold rounded-md px-3 py-2" data-testid={`edit-${p.slug}`}><Edit3 size={12}/> Editar</Link>
                      <button onClick={() => del(p)} className="text-red-500 hover:bg-red-50 w-8 h-8 rounded flex items-center justify-center" title="Eliminar" data-testid={`delete-${p.slug}`}><Trash2 size={13}/></button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-10 bg-white rounded-2xl border border-[#58677A]/10 text-center text-[#58677A]/60 text-sm">No hay proyectos con los filtros aplicados.</div>
        )}
      </main>
    </div>
  );
}

const inputCls = 'w-full border border-[#58677A]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ED6B24] focus:ring-2 focus:ring-[#ED6B24]/10 bg-white text-[#58677A]';

const FilterChip = ({ active, onClick, count, label }) => (
  <button
    onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${active ? 'bg-[#ED6B24] text-white' : 'bg-[#f4f5f7] text-[#58677A] hover:bg-[#ED6B24]/10'}`}
    data-testid={`filter-${label}`}
  >
    {label} <span className="opacity-70">({count})</span>
  </button>
);
